/**
 * ArrivalSplash - cold-launch splash: the aperture mark draws itself, then
 * the shutter FIRES — blades click 60° with a flash, like taking a picture —
 * and the whole thing fades to reveal the app. Runs ~1.2s end to end; the
 * timing lives in the .arrival rules in index.css and must stay in step with
 * the two timeouts below.
 * Reduced motion skips it entirely; a tap ends it early.
 *
 * COLD LAUNCH ONLY (BUG-019): it used to replay on every reload, including the
 * service-worker update reload.
 */
import { useEffect, useState } from 'react';

const ARRIVED_KEY = 'momento_arrived';

/**
 * True only on a genuine open. sessionStorage survives a reload (pull to
 * refresh, or the update reload) but dies when the OS kills the app — which is
 * exactly the line between "reloaded" and "opened".
 */
export function shouldArrive(): boolean {
  return sessionStorage.getItem(ARRIVED_KEY) !== 'true';
}

const reduceMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

const BLADES = [
  'M14.31 8l5.74 9.94',
  'M9.69 8h11.48',
  'M7.38 12l5.74-9.94',
  'M9.69 16L3.95 6.06',
  'M14.31 16H2.83',
  'M16.62 12l-5.74 9.94',
];

export default function ArrivalSplash({ onDone }: { onDone: () => void }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // Claim the launch immediately: a reload mid-animation must not replay it.
    sessionStorage.setItem(ARRIVED_KEY, 'true');
    if (reduceMotion) {
      onDone();
      return;
    }
    // Draw ends ~0.57s, shutter fires at 0.62s, flash at 0.70s, then fade.
    const t = setTimeout(() => setLeaving(true), 950);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!leaving) return;
    // Matches the .arrival opacity transition in index.css (0.25s).
    const t = setTimeout(onDone, 260);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving]);

  if (reduceMotion) return null;

  return (
    <div
      className={`arrival ${leaving ? 'arrival-leaving' : ''}`}
      onClick={() => setLeaving(true)}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.9} strokeLinecap="round">
        <circle className="arrival-arc" cx="12" cy="12" r="10" pathLength={100} style={{ '--k': 0 } as React.CSSProperties} />
        <g className="arrival-blades">
          {BLADES.map((d, i) => (
            <path key={d} className="arrival-arc" d={d} pathLength={100} style={{ '--k': i + 1 } as React.CSSProperties} />
          ))}
        </g>
        <circle className="arrival-center" cx="12" cy="12" r="1.7" />
      </svg>
      <div className="arrival-flash" />
    </div>
  );
}
